import React from 'react';

type AppErrorBoundaryProps = {
  children: React.ReactNode;
  /** Called once per crash — wire this to error reporting (PostHog). */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** App name shown in the fallback message. */
  appName?: string;
};

type AppErrorBoundaryState = { hasError: boolean };

/**
 * Last-resort error boundary: a render crash shows a recoverable message
 * instead of a white screen, and reports the error via onError.
 */
export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      this.props.onError?.(error, info);
    } catch {
      // reporting must never crash the fallback itself
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '24px',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '20px', margin: 0 }}>Something went wrong</h1>
        <p style={{ margin: 0, color: '#555' }}>
          {this.props.appName ? `The ${this.props.appName} app` : 'The app'} hit an unexpected
          error. It has been reported automatically.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 20px',
            borderRadius: '8px',
            border: '1px solid #2C2731',
            background: '#2C2731',
            color: '#FFFCEF',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
